import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";

const pin = (official) => L.divIcon({ className: "", iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -26],
  html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${official ? "#f59e0b" : "#0f172a"};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>` });
const me = L.divIcon({ className: "", iconSize: [18, 18], html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 6px rgba(37,99,235,.25)"></div>' });

const Fit = ({ pts, center }) => {
  const map = useMap();
  useEffect(() => { if (pts.length) map.fitBounds(L.latLngBounds([...(center ? pts.slice(0, 8) : pts), ...(center ? [center] : [])]).pad(0.15), { maxZoom: 14 }); }, [map, pts, center]);
  return null;
};

const Info = ({ s }) => <div className="text-sm" style={{ minWidth: 170 }}><b>{s.name}</b><div className="text-xs text-slate-500">{[s.address, s.city, s.state].filter(Boolean).join(", ")}</div>
  {(s.review_count > 0 || s.google_rating) && <div className="text-xs">★ {s.review_avg || s.google_rating} ({s.review_count || s.google_reviews})</div>}{(s.miles ?? s.distance) != null && <div className="text-xs text-blue-700">{s.miles ?? s.distance} mi away</div>}
  <Link to={`/business/${s.slug}`} className="text-xs font-bold text-blue-700">View shop →</Link></div>;

const GoogleMap = ({ items, apiKey, center }) => {
  const ref = useRef(null);
  useEffect(() => {
    const draw = () => {
      const g = window.google.maps, map = new g.Map(ref.current, { center: { lat: 39.5, lng: -98.35 }, zoom: 4, mapTypeControl: false }), b = new g.LatLngBounds(), iw = new g.InfoWindow();
      items.forEach(s => { const m = new g.Marker({ position: { lat: s.lat, lng: s.lng }, map, title: s.name, icon: s.official ? "https://maps.google.com/mapfiles/ms/icons/orange-dot.png" : undefined });
        m.addListener("click", () => { iw.setContent(`<b>${s.name.replace(/</g, "&lt;")}</b><br><a href="/business/${s.slug}">View shop →</a>`); iw.open(map, m); }); b.extend(m.getPosition()); });
      if (center) { new g.Marker({ position: { lat: center[0], lng: center[1] }, map, title: "You" }); b.extend({ lat: center[0], lng: center[1] }); }
      if (items.length) map.fitBounds(b);
    };
    if (window.google?.maps) return draw();
    const id = "gmaps-js"; let sc = document.getElementById(id);
    if (!sc) { sc = document.createElement("script"); sc.id = id; sc.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`; sc.async = true; document.head.appendChild(sc); }
    sc.addEventListener("load", draw);
    return () => sc.removeEventListener("load", draw);
  }, [items, apiKey, center]);
  return <div ref={ref} className="w-full h-full" data-testid="dir-google-map" />;
};

export const ShopMap = ({ items, center }) => {
  const [cfg, setCfg] = useState({ google_key: "" });
  const [kind, setKind] = useState("street");
  useEffect(() => { api.get("/api/dir/map-config").then(r => setCfg(r.data)).catch(() => {}); }, []);
  const geo = items.filter(s => s.lat != null && s.lng != null);
  const pts = geo.map(s => [s.lat, s.lng]);
  return (
    <div className="space-y-2" data-testid="dir-map">
      <div className="flex items-center gap-2 text-xs"><span className="text-slate-500 flex-1" data-testid="dir-map-count">{geo.length} of {items.length} shops on the map{geo.length < items.length ? " (others have no address yet)" : ""}</span>
        {cfg.google_key && ["street", "google"].map(k => <button key={k} onClick={() => setKind(k)} className={`h-8 px-3 rounded-full font-bold ${kind === k ? "bg-slate-900 text-white" : "border bg-white"}`} data-testid={`dir-map-${k}`}>{k === "street" ? "Street map" : "Google Maps"}</button>)}</div>
      <div className="h-[560px] rounded-2xl overflow-hidden border border-slate-200 relative z-0">
        {kind === "google" && cfg.google_key ? <GoogleMap items={geo} apiKey={cfg.google_key} center={center} /> :
          <MapContainer center={[39.5, -98.35]} zoom={4} scrollWheelZoom className="w-full h-full" data-testid="dir-street-map">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Fit pts={pts} center={center} />{center && <Marker position={center} icon={me}><Popup>You are here</Popup></Marker>}
            {geo.map(s => <Marker key={s.id} position={[s.lat, s.lng]} icon={pin(s.official)}><Popup><Info s={s} /></Popup></Marker>)}
          </MapContainer>}
      </div>
    </div>
  );
};
